import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PlusIcon as Plus, TrashIcon as Trash, PencilSimpleIcon as PencilSimple, TimerIcon as Timer, CalendarBlankIcon as CalendarBlank, ConfettiIcon as Confetti, ArrowsClockwiseIcon as ArrowsClockwise } from "@phosphor-icons/react";

const DAYS = [null, "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const COND_FIELDS = [
  { key: "priorita", label: "Priorità", coll: "priorities" },
  { key: "sorgente", label: "Sorgente", coll: "sources" },
  { key: "gruppo", label: "Gruppo", coll: "groups" },
  { key: "cliente", label: "Cliente", coll: "clients" },
  { key: "contratto_presente", label: "Contratto collegato presente", coll: null },
];
const mins2h = (m) => (m == null ? "—" : m % 60 === 0 ? `${m / 60}h` : `${Math.floor(m / 60)}h ${m % 60}m`);

// ===========================================================================
// POLICY SLA
// ===========================================================================
function ChipMulti({ options, value, onChange, testid }) {
  const arr = Array.isArray(value) ? value : value ? [value] : [];
  const toggle = (id) => onChange(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map((o) => (
        <button key={o.id} type="button" onClick={() => toggle(o.id)}
          className={`text-xs px-2 py-1 rounded-sm border transition-colors ${arr.includes(o.id) ? "bg-blue-600 text-white border-blue-600" : "bg-white border-border hover:bg-secondary"}`}>
          {o.nome || o.ragione_sociale || o.name}
        </button>
      ))}
    </div>
  );
}

function PolicyDialog({ open, onClose, policy, calendars, refs, onSaved }) {
  const [f, setF] = useState(policy);
  const [activeConds, setActiveConds] = useState([]);
  useEffect(() => { setF(policy); setActiveConds((policy?.condizioni_json || []).map((c) => c.field)); }, [policy]);
  if (!f) return null;
  const upd = (k, v) => setF({ ...f, [k]: v });
  const setCond = (field, value) => {
    const others = (f.condizioni_json || []).filter((c) => c.field !== field);
    if (field === "contratto_presente") { setF({ ...f, condizioni_json: [...others, { field, op: "eq", value: true }] }); return; }
    const list = Array.isArray(value) ? value : [value];
    if (!list.length) { setF({ ...f, condizioni_json: others }); return; }
    setF({ ...f, condizioni_json: [...others, { field, op: list.length > 1 ? "in" : "eq", value: list.length > 1 ? list : list[0] }] });
  };
  const condVal = (field) => {
    const c = (f.condizioni_json || []).find((x) => x.field === field);
    if (!c) return [];
    return Array.isArray(c.value) ? c.value : [c.value];
  };
  const hasCond = (field) => (f.condizioni_json || []).some((x) => x.field === field);

  const save = async () => {
    if (!(f.nome || "").trim()) { toast.error("Nome obbligatorio"); return; }
    const body = { ...f, condizioni_json: (f.condizioni_json || []).filter((c) => activeConds.includes(c.field)) };
    try {
      if (f.id) await api.put(`/hd-sla-policies/${f.id}`, body);
      else await api.post("/hd-sla-policies", body);
      toast.success("Policy salvata"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sla-policy-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Modifica policy SLA" : "Nuova policy SLA"}</DialogTitle>
          <DialogDescription>Definisci tempi, warning, calendario e condizioni di applicazione.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Nome *</Label><Input value={f.nome || ""} onChange={(e) => upd("nome", e.target.value)} className="mt-1 rounded-sm" data-testid="sla-policy-nome" /></div>
            <div><Label className="text-xs">Ordine (priorità match)</Label><Input type="number" value={f.ordine ?? 100} onChange={(e) => upd("ordine", parseInt(e.target.value) || 0)} className="mt-1 rounded-sm" /></div>
          </div>
          <div><Label className="text-xs">Descrizione</Label><Input value={f.descrizione || ""} onChange={(e) => upd("descrizione", e.target.value)} className="mt-1 rounded-sm" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Colore</Label><Input type="color" value={f.colore || "#3b82f6"} onChange={(e) => upd("colore", e.target.value)} className="mt-1 rounded-sm h-9 w-full" /></div>
            <div><Label className="text-xs">Calendario lavorativo</Label>
              <Select value={f.calendario_lavorativo_id || ""} onValueChange={(v) => upd("calendario_lavorativo_id", v)}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Predefinito" /></SelectTrigger>
                <SelectContent>{calendars.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Prima risposta (minuti)</Label><Input type="number" min="0" value={f.tempo_prima_risposta_minuti ?? ""} onChange={(e) => upd("tempo_prima_risposta_minuti", e.target.value === "" ? null : parseInt(e.target.value))} className="mt-1 rounded-sm" data-testid="sla-fr-min" /></div>
            <div><Label className="text-xs">Risoluzione (minuti)</Label><Input type="number" min="0" value={f.tempo_risoluzione_minuti ?? ""} onChange={(e) => upd("tempo_risoluzione_minuti", e.target.value === "" ? null : parseInt(e.target.value))} className="mt-1 rounded-sm" data-testid="sla-res-min" /></div>
            <div><Label className="text-xs">Warning prima risposta (min)</Label><Input type="number" min="0" value={f.warning_prima_risposta_minuti ?? 60} onChange={(e) => upd("warning_prima_risposta_minuti", parseInt(e.target.value) || 0)} className="mt-1 rounded-sm" /></div>
            <div><Label className="text-xs">Warning risoluzione (min)</Label><Input type="number" min="0" value={f.warning_risoluzione_minuti ?? 120} onChange={(e) => upd("warning_risoluzione_minuti", parseInt(e.target.value) || 0)} className="mt-1 rounded-sm" /></div>
          </div>
          <div className="flex items-center gap-6 flex-wrap">
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!f.attiva} onCheckedChange={(v) => upd("attiva", v)} data-testid="sla-policy-attiva" /> Attiva</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!f.default_policy} onCheckedChange={(v) => upd("default_policy", v)} data-testid="sla-policy-default" /> Policy predefinita</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.applica_su_ore_lavorative !== false} onCheckedChange={(v) => upd("applica_su_ore_lavorative", v)} /> Calcola su ore lavorative</label>
          </div>
          <div className="pt-2 border-t border-border">
            <Label className="text-xs font-semibold">Stati che sospendono lo SLA</Label>
            <div className="mt-1"><ChipMulti options={refs.statuses} value={f.sospendi_su_stati_ids || []} onChange={(v) => upd("sospendi_su_stati_ids", v)} testid="sla-suspend-states" /></div>
          </div>
          <div>
            <Label className="text-xs font-semibold">Stati che chiudono lo SLA (oltre agli stati finali)</Label>
            <div className="mt-1"><ChipMulti options={refs.statuses} value={f.chiudi_su_stati_ids || []} onChange={(v) => upd("chiudi_su_stati_ids", v)} /></div>
          </div>
          <div className="pt-2 border-t border-border">
            <Label className="text-xs font-semibold">Condizioni di applicazione</Label>
            <p className="text-[11px] text-muted-foreground mb-2">Se nessuna condizione, la policy vale come predefinita. Se più policy combaciano, vince quella con ordine più basso.</p>
            <div className="space-y-2">
              {COND_FIELDS.map((cf) => {
                const on = activeConds.includes(cf.key);
                return (
                  <div key={cf.key} className="flex items-start gap-3">
                    <label className="flex items-center gap-2 text-xs min-w-[190px] pt-1">
                      <Switch checked={on} onCheckedChange={(v) => { setActiveConds(v ? [...activeConds, cf.key] : activeConds.filter((x) => x !== cf.key)); if (v && !hasCond(cf.key)) setCond(cf.key, cf.key === "contratto_presente" ? true : []); }} />
                      {cf.label}
                    </label>
                    {on && cf.coll && (
                      <div className="flex-1"><ChipMulti options={refs[cf.coll] || []} value={condVal(cf.key)} onChange={(v) => setCond(cf.key, v)} /></div>
                    )}
                    {on && !cf.coll && <span className="text-xs text-muted-foreground pt-1.5">Richiede contratto collegato</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Annulla</Button>
          <Button onClick={save} className="rounded-sm" data-testid="sla-policy-save">Salva policy</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PoliciesManager({ refs, calendars }) {
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = useCallback(() => api.get("/hd-sla-policies").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const blank = { nome: "", descrizione: "", attiva: true, ordine: 100, colore: "#3b82f6", default_policy: false,
    applica_su_ore_lavorative: true, calendario_lavorativo_id: (calendars[0] || {}).id || "",
    tempo_prima_risposta_minuti: 240, tempo_risoluzione_minuti: 960, warning_prima_risposta_minuti: 60,
    warning_risoluzione_minuti: 120, sospendi_su_stati_ids: [], chiudi_su_stati_ids: [], condizioni_json: [] };
  const del = async (p) => { if (!window.confirm(`Eliminare la policy "${p.nome}"?`)) return; try { await api.delete(`/hd-sla-policies/${p.id}`); toast.success("Eliminata"); load(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore"); } };

  return (
    <Card className="rounded-sm shadow-none p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-base font-semibold flex items-center gap-2"><Timer size={17} /> Policy SLA</h3>
        <Button size="sm" onClick={() => setDlg(blank)} className="rounded-sm gap-1" data-testid="sla-new-policy"><Plus size={15} /> Nuova policy</Button>
      </div>
      <div className="divide-y divide-border border border-border rounded-sm">
        {items.length === 0 && <p className="text-sm text-muted-foreground p-4">Nessuna policy.</p>}
        {items.map((p) => (
          <div key={p.id} className="flex items-center gap-3 p-3" data-testid={`sla-policy-row-${p.id}`}>
            <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: p.colore || "#94a3b8" }} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium flex items-center gap-2">{p.nome}
                {p.default_policy && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-700">Predefinita</span>}
                {!p.attiva && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-500">Disattivata</span>}
              </div>
              <div className="text-xs text-muted-foreground">Prima risposta {mins2h(p.tempo_prima_risposta_minuti)} · Risoluzione {mins2h(p.tempo_risoluzione_minuti)} · ordine {p.ordine}</div>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDlg(p)} data-testid={`sla-edit-${p.id}`}><PencilSimple size={15} /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(p)} data-testid={`sla-del-${p.id}`}><Trash size={15} /></Button>
          </div>
        ))}
      </div>
      {dlg && <PolicyDialog open={!!dlg} onClose={() => setDlg(null)} policy={dlg} calendars={calendars} refs={refs} onSaved={load} />}
    </Card>
  );
}

// ===========================================================================
// CALENDARI LAVORATIVI
// ===========================================================================
function HoursEditor({ calendarId }) {
  const [hours, setHours] = useState([]);
  const load = useCallback(() => api.get(`/hd-working-calendars/${calendarId}/hours`).then(({ data }) => {
    const merged = [1, 2, 3, 4, 5, 6, 7].map((wd) => {
      const row = data.find((x) => x.giorno_settimana === wd);
      return row || { giorno_settimana: wd, attivo: false, ora_inizio_mattina: "09:00", ora_fine_mattina: "13:00", ora_inizio_pomeriggio: "14:00", ora_fine_pomeriggio: "18:00" };
    });
    setHours(merged);
  }).catch(() => {}), [calendarId]);
  useEffect(() => { load(); }, [load]);
  const upd = (wd, k, v) => setHours(hours.map((h) => h.giorno_settimana === wd ? { ...h, [k]: v } : h));
  const save = async () => { try { await api.put(`/hd-working-calendars/${calendarId}/hours`, { hours }); toast.success("Orari salvati"); } catch { toast.error("Errore"); } };
  return (
    <div className="space-y-2">
      {[1, 2, 3, 4, 5, 6, 7].map((wd) => {
        const h = hours.find((x) => x.giorno_settimana === wd) || { giorno_settimana: wd, attivo: false };
        return (
          <div key={wd} className="flex items-center gap-2 text-sm" data-testid={`cal-day-${wd}`}>
            <label className="flex items-center gap-2 w-32"><Switch checked={!!h.attivo} onCheckedChange={(v) => upd(wd, "attivo", v)} /> {DAYS[wd]}</label>
            {h.attivo ? (
              <>
                <Input type="time" value={h.ora_inizio_mattina || ""} onChange={(e) => upd(wd, "ora_inizio_mattina", e.target.value)} className="rounded-sm h-8 w-28" />
                <Input type="time" value={h.ora_fine_mattina || ""} onChange={(e) => upd(wd, "ora_fine_mattina", e.target.value)} className="rounded-sm h-8 w-28" />
                <span className="text-muted-foreground text-xs">pausa</span>
                <Input type="time" value={h.ora_inizio_pomeriggio || ""} onChange={(e) => upd(wd, "ora_inizio_pomeriggio", e.target.value)} className="rounded-sm h-8 w-28" />
                <Input type="time" value={h.ora_fine_pomeriggio || ""} onChange={(e) => upd(wd, "ora_fine_pomeriggio", e.target.value)} className="rounded-sm h-8 w-28" />
              </>
            ) : <span className="text-xs text-muted-foreground">Non lavorativo</span>}
          </div>
        );
      })}
      <Button size="sm" onClick={save} className="rounded-sm mt-1" data-testid="cal-hours-save">Salva orari</Button>
    </div>
  );
}

function HolidaysEditor({ calendarId }) {
  const [items, setItems] = useState([]);
  const [nf, setNf] = useState({ data: "", nome: "", ricorrente_annuale: true, tipo: "festivita" });
  const load = useCallback(() => api.get(`/hd-working-calendars/${calendarId}/holidays`).then(({ data }) => setItems(data)).catch(() => {}), [calendarId]);
  useEffect(() => { load(); }, [load]);
  const add = async () => { if (!nf.data) { toast.error("Data obbligatoria"); return; } try { await api.post(`/hd-working-calendars/${calendarId}/holidays`, nf); setNf({ data: "", nome: "", ricorrente_annuale: true, tipo: "festivita" }); load(); } catch { toast.error("Errore"); } };
  const del = async (h) => { try { await api.delete(`/hd-working-calendars/${calendarId}/holidays/${h.id}`); load(); } catch { toast.error("Errore"); } };
  const preload = async () => { try { const { data } = await api.post(`/hd-working-calendars/${calendarId}/holidays/preload-italian`); toast.success(`${data.aggiunte} festività aggiunte`); load(); } catch { toast.error("Errore"); } };
  return (
    <div className="space-y-3">
      <div className="flex items-end gap-2 flex-wrap">
        <div><Label className="text-xs">Data</Label><Input type="date" value={nf.data} onChange={(e) => setNf({ ...nf, data: e.target.value })} className="rounded-sm h-9 w-40" data-testid="holiday-data" /></div>
        <div><Label className="text-xs">Nome</Label><Input value={nf.nome} onChange={(e) => setNf({ ...nf, nome: e.target.value })} className="rounded-sm h-9 w-48" placeholder="Es. Chiusura estiva" data-testid="holiday-nome" /></div>
        <div><Label className="text-xs">Tipo</Label>
          <Select value={nf.tipo} onValueChange={(v) => setNf({ ...nf, tipo: v })}><SelectTrigger className="rounded-sm h-9 w-40"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="festivita">Festività</SelectItem><SelectItem value="chiusura_aziendale">Chiusura aziendale</SelectItem><SelectItem value="altro">Altro</SelectItem></SelectContent></Select>
        </div>
        <label className="flex items-center gap-2 text-sm h-9"><Switch checked={nf.ricorrente_annuale} onCheckedChange={(v) => setNf({ ...nf, ricorrente_annuale: v })} /> Ricorrente</label>
        <Button size="sm" onClick={add} className="rounded-sm gap-1 h-9" data-testid="holiday-add"><Plus size={15} /> Aggiungi</Button>
        <Button size="sm" variant="outline" onClick={preload} className="rounded-sm h-9" data-testid="holiday-preload">Precarica festività IT</Button>
      </div>
      <div className="divide-y divide-border border border-border rounded-sm max-h-72 overflow-y-auto">
        {items.length === 0 && <p className="text-sm text-muted-foreground p-3">Nessuna festività.</p>}
        {items.map((h) => (
          <div key={h.id} className="flex items-center gap-2 p-2 text-sm" data-testid={`holiday-${h.id}`}>
            <span className="font-mono text-xs w-24">{h.ricorrente_annuale ? h.data.slice(5) : h.data}</span>
            <span className="flex-1">{h.nome}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600">{h.tipo}</span>
            {h.ricorrente_annuale && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-600">annuale</span>}
            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => del(h)} data-testid={`holiday-del-${h.id}`}><Trash size={14} /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CalendarsManager({ onCalendarsChange }) {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [newName, setNewName] = useState("");
  const load = useCallback(() => api.get("/hd-working-calendars").then(({ data }) => { setItems(data); onCalendarsChange?.(data); if (!sel && data.length) setSel(data[0].id); }).catch(() => {}), [sel, onCalendarsChange]);
  useEffect(() => { load(); }, []); // eslint-disable-line
  const create = async () => { if (!newName.trim()) { toast.error("Nome obbligatorio"); return; } try { const { data } = await api.post("/hd-working-calendars", { nome: newName }); setNewName(""); setSel(data.id); load(); toast.success("Calendario creato"); } catch { toast.error("Errore"); } };
  const patch = async (cid, k, v) => { try { await api.put(`/hd-working-calendars/${cid}`, { [k]: v }); load(); } catch { toast.error("Errore"); } };
  const del = async (c) => { if (!window.confirm(`Eliminare "${c.nome}"?`)) return; try { await api.delete(`/hd-working-calendars/${c.id}`); setSel(null); load(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore"); } };
  const cur = items.find((c) => c.id === sel);
  return (
    <Card className="rounded-sm shadow-none p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-base font-semibold flex items-center gap-2"><CalendarBlank size={17} /> Calendari lavorativi</h3>
        <div className="flex gap-2">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nome nuovo calendario" className="rounded-sm h-9 w-56" data-testid="cal-new-name" />
          <Button size="sm" onClick={create} className="rounded-sm gap-1 h-9" data-testid="cal-create"><Plus size={15} /> Crea</Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {items.map((c) => (
          <button key={c.id} onClick={() => setSel(c.id)} className={`text-sm px-3 py-1.5 rounded-sm border ${sel === c.id ? "bg-blue-600 text-white border-blue-600" : "bg-white border-border hover:bg-secondary"}`} data-testid={`cal-tab-${c.id}`}>
            {c.nome}{c.default_calendar && " ★"}
          </button>
        ))}
      </div>
      {cur && (
        <div className="space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div><Label className="text-xs">Nome</Label><Input value={cur.nome} onChange={(e) => setItems(items.map((x) => x.id === cur.id ? { ...x, nome: e.target.value } : x))} onBlur={(e) => patch(cur.id, "nome", e.target.value)} className="rounded-sm h-9 w-56" /></div>
            <div><Label className="text-xs">Timezone</Label><Input value={cur.timezone} onChange={(e) => setItems(items.map((x) => x.id === cur.id ? { ...x, timezone: e.target.value } : x))} onBlur={(e) => patch(cur.id, "timezone", e.target.value)} className="rounded-sm h-9 w-48" /></div>
            <label className="flex items-center gap-2 text-sm mt-4"><Switch checked={!!cur.attivo} onCheckedChange={(v) => patch(cur.id, "attivo", v)} /> Attivo</label>
            <label className="flex items-center gap-2 text-sm mt-4"><Switch checked={!!cur.default_calendar} onCheckedChange={(v) => patch(cur.id, "default_calendar", v)} data-testid="cal-default" /> Predefinito</label>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive mt-4" onClick={() => del(cur)} data-testid={`cal-del-${cur.id}`}><Trash size={15} /></Button>
          </div>
          <div className="border-t border-border pt-3">
            <Label className="text-xs font-semibold mb-2 block">Orari lavorativi</Label>
            <HoursEditor calendarId={cur.id} />
          </div>
          <div className="border-t border-border pt-3">
            <Label className="text-xs font-semibold mb-2 block flex items-center gap-1.5"><Confetti size={14} /> Festività e chiusure</Label>
            <HolidaysEditor calendarId={cur.id} />
          </div>
        </div>
      )}
    </Card>
  );
}

// ===========================================================================
// ROOT
// ===========================================================================
export function SlaSettings({ refs }) {
  const [calendars, setCalendars] = useState([]);
  useEffect(() => { api.get("/hd-working-calendars").then(({ data }) => setCalendars(data)).catch(() => {}); }, []);
  const recalcAll = async () => { try { const { data } = await api.post("/hd-sla/recalculate-open"); toast.success(`${data.aggiornati} ticket ricalcolati`); } catch { toast.error("Errore"); } };
  return (
    <div className="space-y-4" data-testid="sla-settings">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={recalcAll} className="rounded-sm gap-1.5" data-testid="sla-recalc-all"><ArrowsClockwise size={15} /> Ricalcola SLA ticket aperti</Button>
      </div>
      <Tabs defaultValue="policy">
        <TabsList className="rounded-sm">
          <TabsTrigger value="policy" data-testid="sla-tab-policy"><Timer size={15} className="mr-1.5" />Policy</TabsTrigger>
          <TabsTrigger value="calendari" data-testid="sla-tab-calendari"><CalendarBlank size={15} className="mr-1.5" />Calendari & Festività</TabsTrigger>
        </TabsList>
        <TabsContent value="policy" className="mt-4"><PoliciesManager refs={refs} calendars={calendars} /></TabsContent>
        <TabsContent value="calendari" className="mt-4"><CalendarsManager onCalendarsChange={setCalendars} /></TabsContent>
      </Tabs>
    </div>
  );
}
